import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"


import { Button } from "@/components/Button/Button.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { ArrowUpMedium, Icon20 } from "@/icons/Icons.ts"

import type { PromptInputModeType } from "@/components/PromptInput/PromptInputModel.ts"
import type { ButtonHTMLAttributes, MouseEvent } from "react"

type ButtonSendProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  mode?: PromptInputModeType
  stopAction?: () => void
  canSubmit?: boolean
}

export const ButtonSend = ({ mode, stopAction, ...props }: ButtonSendProps) => {
  const showStopButton = !!stopAction // && !canSubmit

  const buttonContent = showStopButton ? (
    <Stop />
  ) : (
    <ArrowUpMedium className={Icon20} color="var(--main-color)" />
  )

  const sendOrStopButtonProps = showStopButton
    ? {
      "data-testid": "stop-button" as const,
      onClick: (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        stopAction?.()
      },
    }
    : props

  switch (mode) {
    case "InviteDeveloper":
      return (
        <SendInviteButton kind="alternative" size="default" {...props}>
          Send invite
        </SendInviteButton>
      )
    case "RequestSupport":
      return (
        <RequestSupportButton kind="alternative" size="default" {...props}>
          Request support
        </RequestSupportButton>
      )

    default:
      return (
        <Tooltip content={showStopButton ? "Stop" : undefined}>
          <SendOrStopButton
            kind="icon-rounded"
            size="large"
            isStop={showStopButton ?? false}
            className={cx(stopAction ? BorderSpinner : undefined)}
            {...sendOrStopButtonProps}
          >
            {buttonContent}
          </SendOrStopButton>
        </Tooltip>
      )
  }
}

const BorderSpinner = css`
  position: relative;

  &::before {
    content: "";

    position: absolute;
    inset: -1.5px;
    padding: 1.5px;

    border-radius: inherit;

    background: conic-gradient(
      from 0deg,
      oklch(100% 0 0deg) 0%,
      oklch(100% 0 0deg) 50%,
      var(--main-color) 100%
    );

    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: rotate-border 1.2s linear infinite, fade-in-border 0.3s ease-out forwards;
  }

  @keyframes rotate-border {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  @keyframes fade-in-border {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`

const SendButton = styled(Button)`
  box-shadow: 0 4px 20px 0 var(--text-color-10);
  transition: box-shadow 0.3s ease-out;

  &:not(:disabled) {
    box-shadow: inset 0 1px 2px var(--background-color-25), 0 0 15px oklch(73.4% 0.15 291.466deg / 30%);
  }

  &:hover:not(:disabled) {
    box-shadow: inset 0 1px 2px var(--background-color-25), 0 0 20px oklch(51.7% 0.148 289.209deg / 30%);
  }

  &:active:not(:disabled) {
    box-shadow: inset 0 1px 2px var(--background-color-25), 0 0 15px oklch(73.4% 0.15 291.466deg / 30%);
  }
`

const SendOrStopButton = styled(SendButton)<{ isStop: boolean }>`
  background: ${props => props.isStop ? "var(--background-color)" : "transparent"};
  transition: box-shadow 0.3s ease-out, background 0.3s ease-out;

  .svg-icon {
    transition: color 0.3s ease-out;
  }

  &:disabled {
    box-shadow: 0 4px 20px 0 var(--text-color-10);

    .svg-icon {
      color: var(--text-color-30);
    }
  }
`

const SendInviteButton = styled(SendButton)`
  color: var(--main-color);

  &:hover:not(:disabled) {
    color: var(--main-color);
  }
`

const RequestSupportButton = styled(SendButton)`
  color: var(--main-color);

  &:hover:not(:disabled) {
    color: var(--main-color);
  }
`

const Stop = styled.span`
  display: inline-block;
  height: 12px;
  width: 12px;

  background: var(--main-color);
  border-radius: var(--border-radius-4);
`
